import { getLuminance } from "@mui/material";
import { dateToIsoInTz, parseDate, serializeDate } from "@notemeal/utils-date-time";
import { addDays } from "date-fns";
import { CalendarEvent, getCalendarEventsOnDate, getCalendarEventsWithSlots } from "../../../components/Calendar/Week/utils";
import { MealType } from "../../../types";

const DEFAULT_BREAKFAST_HUE = 0;
const DEFAULT_LUNCH_HUE = 90;
const DEFAULT_DINNER_HUE = 180;
const DEFAULT_SNACK_HUE = 270;

const HUE_INTERVAL = 70;
const HUE_LIMIT = 360;

const black = "#000000";
const white = "#ffffff";

export const getHueForMealMenu = (mealType: MealType, slot: number) => {
  const defaultHue =
    mealType === "breakfast"
      ? DEFAULT_BREAKFAST_HUE
      : mealType === "lunch"
      ? DEFAULT_LUNCH_HUE
      : mealType === "dinner"
      ? DEFAULT_DINNER_HUE
      : DEFAULT_SNACK_HUE;
  return (defaultHue + slot * HUE_INTERVAL) % HUE_LIMIT;
};

/**
 * Picks the overlap slot each menu should be colored by, keyed by menu id.
 *
 * To keep an overnight menu one color, we always color by the slot from the day the
 * menu *starts*.
 *
 * Time only enters here to answer one question: "did this menu start today, or is
 * today just a continuation of an earlier day?"
 */
export const getMealMenuColorSlots = (
  events: readonly CalendarEvent[],
  weekStart: string,
  clientTimezone: string
): ReadonlyMap<string, number> => {
  const colorSlots = new Map<string, number>();
  const weekStartDate = parseDate(weekStart);

  for (let day = 0; day < 7; day++) {
    const date = serializeDate(addDays(weekStartDate, day));
    // Midnight of this day in the viewer's timezone — the boundary between "starts today" and "continuation".
    const dateStart = new Date(dateToIsoInTz(date, clientTimezone));
    const eventsWithSlots = getCalendarEventsWithSlots(getCalendarEventsOnDate(events, date, clientTimezone));

    for (const { event, slot } of eventsWithSlots) {
      if (event.start >= dateStart) {
        colorSlots.set(event.id, slot);
      }
    }
  }

  return colorSlots;
};

export const getTextColor = (colorString: string): string => {
  const luminance = getLuminance(colorString);
  const whiteLuminance = getLuminance(white);
  const blackLuminance = getLuminance(black);

  const whiteContrast = (whiteLuminance + 0.05) / (luminance + 0.05);
  const blackContrast = (luminance + 0.05) / (blackLuminance + 0.05);

  return whiteContrast > blackContrast ? white : black;
};

export const getMealMenuCalendarLabel = (name: string, isContinuation: boolean): string => (isContinuation ? `${name} (Cont.)` : name);
